<template>
	<div id="full-editor" class="tiptap-editor">
		<div id="editor-controls">
			<div v-if="editor">
				<b-button-toolbar key-nav>
					<b-button-group class="mr-1">
						<b-button v-b-tooltip.hover title="Insert Image" @click.prevent="addImage">
							<font-awesome-icon :icon="['fas', 'image']" />
						</b-button>
					</b-button-group>
					<b-button-group class="mr-1">
						<b-button v-b-tooltip.hover title="Bold" :class="{ 'active': editor.isActive('bold') }" @click.prevent="editor.chain().focus().toggleBold().run()">
							<font-awesome-icon :icon="['fas', 'bold']" />
						</b-button>
						<b-button v-b-tooltip.hover title="Italic" :class="{ 'active': editor.isActive('italic') }" @click.prevent="editor.chain().focus().toggleItalic().run()">
							<font-awesome-icon :icon="['fas', 'italic']" />
						</b-button>
						<b-button v-b-tooltip.hover title="Strikethrough" :class="{ 'active': editor.isActive('strike') }" @click.prevent="editor.chain().focus().toggleStrike().run()">
							<font-awesome-icon :icon="['fas', 'strikethrough']" />
						</b-button>
					</b-button-group>
					<b-button-group class="mr-1">
						<b-button v-b-tooltip.hover title="Code" :class="{ 'active': editor.isActive('code') }" @click.prevent="editor.chain().focus().toggleCode().run()">
							<font-awesome-icon :icon="['fas', 'code']" />
						</b-button>
						<b-dropdown v-b-tooltip.hover
							class="editor-menu"
							title="Paragraph"
							:toggle-class="{ 'active': editor.isActive('paragraph') }"
							:split-class="{ 'active': editor.isActive('paragraph') }"
							split
							right
							@click.prevent="editor.chain().focus().setParagraph().run()">
							<template #button-content>
								<font-awesome-icon :icon="['fas', 'paragraph']" />
							</template>
							<b-dropdown-text>
								<input
									v-model="textColor"
									type="color"
									class="mb-2 pb-0"
									placeholder="#000000" />
								<b-button
									class="text-nowrap"
									variant="secondary"
									size="sm"
									@click.prevent="editor.chain().focus().updateAttributes('paragraph', {'style': 'color:' + textColor}).run()">
									Set Paragraph Color
								</b-button>
							</b-dropdown-text>
							<b-dropdown-divider />
							<b-dropdown-text>
								<div style="width: 175px;">
									<b-form-select v-model="fontSize"
										:options="fontSizes"
										@change="setFontSize('paragraph')" />
								</div>
							</b-dropdown-text>
						</b-dropdown>
						<b-dropdown v-b-tooltip.hover title="Heading" :toggle-class="{ 'active': editor.isActive('heading') }" left>
							<template #button-content>
								<span class="mr-2"><font-awesome-icon :icon="['fas', 'heading']" /></span>
							</template>
							<b-dropdown-item @click.prevent="editor.chain().focus().toggleHeading({ level: 1 }).run()"><h1>H1 Heading</h1></b-dropdown-item>
							<b-dropdown-item @click.prevent="editor.chain().focus().toggleHeading({ level: 2 }).run()"><h2>H2 Heading</h2></b-dropdown-item>
							<b-dropdown-item @click.prevent="editor.chain().focus().toggleHeading({ level: 3 }).run()"><h3>H3 Heading</h3></b-dropdown-item>
							<b-dropdown-item @click.prevent="editor.chain().focus().toggleHeading({ level: 4 }).run()"><h4>H4 Heading</h4></b-dropdown-item>
							<b-dropdown-item @click.prevent="editor.chain().focus().toggleHeading({ level: 5 }).run()"><h5>H5 Heading</h5></b-dropdown-item>
							<b-dropdown-item @click.prevent="editor.chain().focus().toggleHeading({ level: 6 }).run()"><h6>H6 Heading</h6></b-dropdown-item>
						</b-dropdown>
						<b-button v-b-tooltip.hover title="Hard Break" @click.prevent="editor.chain().focus().setHardBreak().run()">
							<font-awesome-icon :icon="['fas', 'grip-lines']" />
						</b-button>
						<b-button @click.prevent="editor.chain().focus().setHorizontalRule().run()">
							<strong>&horbar;</strong>
						</b-button>
					</b-button-group>
					<b-button-group class="mr-1">
						<b-dropdown v-b-tooltip.hover
							class="editor-menu"
							title="Bullet List"
							:toggle-class="{ 'active': editor.isActive('bulletList') }"
							:split-class="{ 'active': editor.isActive('bulletList') }"
							split
							right
							@click.prevent="editor.chain().focus().toggleBulletList().run()">
							<template #button-content>
								<font-awesome-icon :icon="['fas', 'list']" />
							</template>
							<b-dropdown-text>
								<div style="width: 175px;">
									<b-form-select v-model="fontSize"
										:options="fontSizes"
										@change="setFontSize('bulletList')" />
								</div>
							</b-dropdown-text>
						</b-dropdown>
						<b-dropdown v-b-tooltip.hover
							class="editor-menu"
							title="Ordered List"
							:toggle-class="{ 'active': editor.isActive('orderedList') }"
							:split-class="{ 'active': editor.isActive('orderedList') }"
							split
							right
							@click.prevent="editor.chain().focus().toggleOrderedList().run()">
							<template #button-content>
								<font-awesome-icon :icon="['fas', 'list-ol']" />
							</template>
							<b-dropdown-text>
								<div style="width: 175px;">
									<b-form-select v-model="fontSize"
										:options="fontSizes"
										@change="setFontSize('orderedList')" />
								</div>
							</b-dropdown-text>
						</b-dropdown>
					</b-button-group>
					<b-dropdown id="table-menu"
						v-b-tooltip.hover
						class="mr-1"
						title="Table"
						:toggle-class="{ 'active': editor.isActive('table') }"
						:split-class="{ 'active': editor.isActive('table') }"
						split
						right
						@click.prevent="editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()">
						<template #button-content>
							<font-awesome-icon :icon="['fas', 'table']" />
						</template>
						<b-dropdown-item @click.prevent="editor.chain().focus().addColumnBefore().run()">Add Column Before</b-dropdown-item>
						<b-dropdown-item @click.prevent="editor.chain().focus().addColumnAfter().run()">Add Column After</b-dropdown-item>
						<b-dropdown-item @click.prevent="editor.chain().focus().deleteColumn().run()">Delete Column</b-dropdown-item>
						<b-dropdown-item @click.prevent="editor.chain().focus().addRowBefore().run()">Add Row Before</b-dropdown-item>
						<b-dropdown-item @click.prevent="editor.chain().focus().addRowAfter().run()">Add Row After</b-dropdown-item>
						<b-dropdown-item link-class="text-danger" @click.prevent="editor.chain().focus().deleteRow().run()">Delete Row</b-dropdown-item>
						<b-dropdown-item link-class="text-danger" @click.prevent="editor.chain().focus().deleteTable().run()">Delete Table</b-dropdown-item>
					</b-dropdown>
					<b-button-group class="mr-1">
						<b-button v-b-tooltip.hover title="Align Left" :class="{ 'is-active': editor.isActive({ textAlign: 'left' }) }" @click.prevent="editor.chain().focus().setTextAlign('left').run()">
							<font-awesome-icon :icon="['fas', 'align-left']" />
						</b-button>
						<b-button v-b-tooltip.hover title="Align Center" :class="{ 'is-active': editor.isActive({ textAlign: 'center' }) }" @click.prevent="editor.chain().focus().setTextAlign('center').run()">
							<font-awesome-icon :icon="['fas', 'align-center']" />
						</b-button>
						<b-button v-b-tooltip.hover title="Align Right" :class="{ 'is-active': editor.isActive({ textAlign: 'right' }) }" @click.prevent="editor.chain().focus().setTextAlign('right').run()">
							<font-awesome-icon :icon="['fas', 'align-right']" />
						</b-button>
					</b-button-group>
					<b-button-group class="mr-1">
						<b-button v-b-tooltip.hover title="Code block" :class="{ 'active': editor.isActive('codeBlock') }" @click.prevent="editor.chain().focus().toggleCodeBlock().run()">
							<font-awesome-icon :icon="['fas', 'code']" />
						</b-button>
						<b-button v-b-tooltip.hover title="Blockquote" :class="{ 'active': editor.isActive('blockquote') }" @click.prevent="editor.chain().focus().toggleBlockquote().run()">
							<font-awesome-icon :icon="['fas', 'quote-left']" />
						</b-button>
					</b-button-group>
					<b-dropdown id="text-color-menu"
						v-b-tooltip.hover
						class="mr-1"
						title="Selected Text Color"
						right>
						<template #button-content>
							<font-awesome-icon :icon="['fas', 'palette']" />
						</template>
						<b-dropdown-text>
							<input
								v-model="textColor"
								type="color"
								class="mb-2 pb-0"
								placeholder="#000000" />
							<b-button
								class="text-nowrap"
								variant="secondary"
								size="sm"
								@click.prevent="editor.chain().focus().setTextColor(textColor).run()">
								Set Text Color
							</b-button>
						</b-dropdown-text>
					</b-dropdown>
					<b-button-group class="ml-auto">
						<b-button v-b-tooltip.hover title="Undo" @click.prevent="editor.chain().focus().undo().run()">
							<font-awesome-icon :icon="['fas', 'undo']" />
						</b-button>
						<b-button v-b-tooltip.hover title="Redo" @click.prevent="editor.chain().focus().redo().run()">
							<font-awesome-icon :icon="['fas', 'redo']" />
						</b-button>
					</b-button-group>
				</b-button-toolbar>
			</div>
		</div>
		<div id="editor-content">
			<div class="editor-content-wrapper">
				<editor-content :editor="editor" />
			</div>
		</div>
	</div>
</template>

<script>
import { Editor, EditorContent } from '@tiptap/vue-2'
import StarterKit from '@tiptap/starter-kit'
import TextStyle from '@tiptap/extension-text-style'
import TextAlign from '@tiptap/extension-text-align'
import { TextColor } from '@/TipTap/TextColor'
import Table from '@tiptap/extension-table'
import TableRow from '@tiptap/extension-table-row'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import Gapcursor from '@tiptap/extension-gapcursor'
import { StyleImage, StyleParagraph, StyleOrderedList, StyleBulletList } from '@/Component/Tiptap/extentions'

export default {
	name: 'FullEditor',

	components: {
		EditorContent
	},

	props: {
		content: {
			type: String,
			default: '',
			required: false
		}
	},

	data () {
		return {
			showHeaderOverlay: false,
			editor: null,
			textColor: '#0080c0',
			fontSize: null,
			fontSizes: [
				{ text: 'Change Font Size', value: null },
				{ text: 'Tiny Font', value: '0.66em' },
				{ text: 'Small Font', value: '0.76em' },
				{ text: 'Normal Font', value: 'unset' },
				{ text: 'Large Font', value: '1.2em' }
			],
			showTextColorPicker: true
		}
	},

	watch: {
		content (value) {
			// HTML
			const isSame = this.editor.getHTML() === value

			if (isSame) {
				return
			}

			this.editor.commands.setContent(this.content, false)
		}
	},

	mounted () {
		this.editor = new Editor({
			extensions: [
				StarterKit,
				TextStyle,
				TextAlign.configure({
					types: ['heading', 'paragraph']
				}),
				StyleParagraph,
				StyleBulletList,
				StyleOrderedList,
				StyleImage,
				TextColor,
				Table.configure({
					resizable: true
				}),
				TableRow,
				TableHeader,
				TableCell,
				Gapcursor
			],
			content: this.content,
			editorProps: {
				attributes: {
					spellcheck: 'false'
				}
			},
			onUpdate: () => {
				this.$emit('input', this.editor.getHTML())
			}
		})
	},

	beforeDestroy () {
		this.editor.destroy()
	},

	methods: {
		addImage () {
			const url = window.prompt('URL')

			if (url) {
				this.editor.chain().focus().setImage({ src: url }).run()
			}
		},

		setColor () {
			this.editor.commands.TextColor({ color: 'red' })
		},

		// Used for shortcodes
		insertText (text) {
			this.editor
				.chain()
				.focus()
				.command(({ tr }) => {
					// manipulate the transaction
					tr.insertText(text)

					return true
				})
				.run()
		},

		setFontSize (node) {
			if (! this.fontSize) {
				return
			}

			this.editor.chain().focus().updateAttributes(node, { 'style': 'font-size:' + this.fontSize }).run()
			this.fontSize = null
		}
	}
}
</script>
